import React from "react";

/**
 * Smart pagination component using sliding window approach.
 * 
 * Navigation buttons: « Trang đầu - ‹ Trang trước - [page numbers] - Trang sau › - Trang cuối »
 * Page cluster: Always shows max 5-7 page buttons, others hidden with ...
 * 
 * When at middle page: « Trang đầu < 1 ... 4 5 6 7 8 ... 20 > Trang cuối »
 * When at first pages: < 1 2 3 4 5 ... 20 > Trang cuối »
 * When at last pages:  « Trang đầu < 1 ... 16 17 18 19 20 >
 *
 * @param {number} currentPage - Current active page (1-indexed)
 * @param {number} totalPages - Total number of pages
 * @param {function} onPageChange - Callback: (event, pageNumber) => void
 */
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  if (!totalPages || totalPages <= 1) return null;

  const getPageNumbers = () => {
    // If total pages <= 7, show all
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages = [];
    const siblingCount = 2; // show 2 pages on each side of current

    const leftSibling = Math.max(currentPage - siblingCount, 1);
    const rightSibling = Math.min(currentPage + siblingCount, totalPages);

    const showLeftDots = leftSibling > 2;
    const showRightDots = rightSibling < totalPages - 1;

    // Always show page 1
    pages.push(1);

    if (showLeftDots) {
      pages.push("...");
    } else {
      // Show pages 2 to leftSibling-1
      for (let i = 2; i < leftSibling; i++) {
        pages.push(i);
      }
    }

    // Sliding window pages
    for (let i = leftSibling; i <= rightSibling; i++) {
      if (i !== 1 && i !== totalPages) {
        pages.push(i);
      }
    }

    if (showRightDots) {
      pages.push("...");
    } else {
      // Show pages rightSibling+1 to totalPages-1
      for (let i = rightSibling + 1; i < totalPages; i++) {
        pages.push(i);
      }
    }

    // Always show last page
    pages.push(totalPages);

    return pages;
  };

  return (
    <div className="d-flex align-items-center justify-content-end mt-3">
      <ul className="pagination">
        {/* Trang đầu */}
        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
          <button className="page-link" onClick={(e) => onPageChange(e, 1)} title="Trang đầu">
            &laquo;
          </button>
        </li>
        {/* Trang trước */}
        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
          <button className="page-link" onClick={(e) => onPageChange(e, currentPage - 1)} title="Trang trước">
            &lsaquo;
          </button>
        </li>

        {/* Page numbers */}
        {getPageNumbers().map((page, idx) =>
          page === "..." ? (
            <li key={`dots-${idx}`} className="page-item disabled">
              <span className="page-link">&hellip;</span>
            </li>
          ) : (
            <li key={page} className={`page-item ${page === currentPage ? "active" : ""}`}>
              <button className="page-link" onClick={(e) => onPageChange(e, page)}>
                {page}
              </button>
            </li>
          )
        )}

        {/* Trang sau */}
        <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
          <button className="page-link" onClick={(e) => onPageChange(e, currentPage + 1)} title="Trang sau">
            &rsaquo;
          </button>
        </li>
        {/* Trang cuối */}
        <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
          <button className="page-link" onClick={(e) => onPageChange(e, totalPages)} title="Trang cuối">
            &raquo;
          </button>
        </li>
      </ul>
    </div>
  );
};

export default Pagination;
